import type { Program } from '@compass/client/types/program';
import type { Workflow } from '@compass/client/types/workflow';
import { workflowService } from '@services/api';
import { queryKeys } from '@services/query.config';
import { useStore } from '@store/index';
import { useQueries } from '@tanstack/react-query';
import { useMemo } from 'react';

export interface MedBVProgramOption {
  program: Program;
  medbvWorkflow: Workflow;
}

/**
 * Hook that fetches workflows for all programs and returns programs that have medbv workflows.
 * Used for the "Move to MedBV" dropdown in medicare_human case display.
 */
export const useMedBVPrograms = () => {
  const selectedOrganizationId = useStore(state => state.selectedOrganizationId);
  const getProgramsArray = useStore(state => state.getProgramsArray);

  const programs = useMemo(() => getProgramsArray(), [getProgramsArray]);

  // Fetch workflows for all programs in parallel
  const workflowQueries = useQueries({
    queries: programs.map(program => ({
      queryKey: queryKeys.workflows.byProgram(program.id || ''),
      queryFn: () =>
        workflowService.getWorkflowsByProgram({
          organization_id: selectedOrganizationId!,
          program_id: program.id!,
        }),
      enabled: !!selectedOrganizationId && !!program.id,
      staleTime: 10 * 60 * 1000, // 10 minutes - workflows don't change often
    })),
  });

  // Combine programs with their medbv workflows
  const medBVProgramOptions = useMemo((): MedBVProgramOption[] => {
    const options: MedBVProgramOption[] = [];

    programs.forEach((program, index) => {
      const queryResult = workflowQueries[index];
      if (queryResult?.data?.workflows) {
        // Find the first medbv workflow for this program
        const medbvWorkflow = queryResult.data.workflows.find(
          workflow => workflow.workflow_type === 'medbv',
        );
        if (medbvWorkflow) {
          options.push({ program, medbvWorkflow });
        }
      }
    });

    return options;
  }, [programs, workflowQueries]);

  const isLoading = workflowQueries.some(query => query.isLoading);
  const isError = workflowQueries.some(query => query.isError);

  return {
    medBVProgramOptions,
    isLoading,
    isError,
    programs,
  };
};
